/**
 * Workspace file alias resolver for the chat applier (workspace-file-aliases SPA
 * spec Part B §B.2). A memoized index DERIVED from the tree LexIDE already
 * fetches (`useCaseWorkspaceTree`) — NO new fetch, NO new store. It maps a
 * file-reference payload (case-relative path or bare basename) to the file's
 * alias + real path so `<FileChip>` renders the friendly name and opens the file.
 *
 * Freshness is pure React-Query invalidation: when the rename UI (Part A) or any
 * file op invalidates the tree query, this index recomputes and every mounted
 * chip re-resolves — THIS is the retroactive-rename property (rename a file →
 * its chips relabel across already-rendered chat history). Do not cache outside
 * React.
 *
 * Files only (chat file references are files). Hidden/internal entries
 * are already filtered server-side (`case-tree.ts`), so the walk sees only
 * user-visible files. Until the runtime P1 gate lands, tree nodes carry no
 * `alias`, so `resolve` returns entries with `alias: undefined` → chips render
 * the bare name (graceful, no-op) and light up the moment P1 ships.
 */

import { useMemo } from "react";
import { useCaseWorkspaceTree } from "./useCaseWorkspaceTree";
import { basename } from "@/stores/useWorkspaceFileStore";
import { normalizeTagKey } from "@/lib/workspace/display-name";
import type { WorkspaceTreeFolder } from "@/types/gateway";

export interface WorkspaceAliasEntry {
  /** Case-root-relative path — the openFile reference (never changes). */
  path: string;
  /** Raw on-disk basename. */
  name: string;
  /** User-facing alias, when set. */
  alias?: string;
  /** User-facing description note, when set (surfaced in the file pane). */
  description?: string;
  /** Workspace-file-tags: the @tag reference handle (stored WITHOUT `@`), when set. */
  tag?: string;
  /** Workspace-file-tags: the friendly citation title (tree line 2), when set. */
  title?: string;
}

/** Sentinel for a basename that occurs in more than one folder. */
const AMBIGUOUS = "ambiguous" as const;

export interface WorkspaceAliasMap {
  byPath: ReadonlyMap<string, WorkspaceAliasEntry>;
  byBasename: ReadonlyMap<string, WorkspaceAliasEntry | typeof AMBIGUOUS>;
  /** Workspace-file-tags: normalized `@tag` key → file entry (case-scope unique). */
  byTag: ReadonlyMap<string, WorkspaceAliasEntry>;
  /**
   * Resolve a tag payload to its file entry, or null on a miss.
   * Order (SPA spec §B.1 / runtime C3): exact case-relative PATH → unique
   * BASENAME → miss. An ambiguous basename (same name in two folders) is a miss.
   */
  resolve(payload: string): WorkspaceAliasEntry | null;
  /**
   * Resolve an `@tag` body (a leading `@` is tolerated) to its file entry, or null
   * on a miss — CLOSED-WORLD (only a registered tag resolves; unknown → null →
   * render literal, analysis §8). Normalized (casefold + accent-strip), so
   * `@Contestação` ≡ `@contestacao`.
   */
  resolveTag(tag: string): WorkspaceAliasEntry | null;
  /** All tagged files (for the composer picker), each with its display `@tag`. */
  allTags(): { entry: WorkspaceAliasEntry; tag: string }[];
  /**
   * Effective display names of the same-directory FILE siblings of `path`
   * (excludes `path` itself) — the D3 uniqueness pre-check set for the file
   * pane's rename popover. NB: folder siblings aren't tracked here (files only),
   * so this is an approximation; the server does the authoritative check.
   */
  siblingEffectiveNames(path: string): string[];
}

/** Directory portion of a case-relative path ("a/b/c.pdf" → "a/b"; "c.pdf" → ""). */
function dirOf(path: string): string {
  const i = path.lastIndexOf("/");
  return i >= 0 ? path.slice(0, i) : "";
}

function walk(
  folder: WorkspaceTreeFolder,
  byPath: Map<string, WorkspaceAliasEntry>,
  byBasename: Map<string, WorkspaceAliasEntry | typeof AMBIGUOUS>,
  byTag: Map<string, WorkspaceAliasEntry>,
): void {
  for (const f of folder.files) {
    const entry: WorkspaceAliasEntry = {
      path: f.path,
      name: f.name,
      alias: f.alias,
      description: f.description,
      tag: f.tag,
      title: f.title,
    };
    byPath.set(f.path, entry);
    byBasename.set(f.name, byBasename.has(f.name) ? AMBIGUOUS : entry);
    if (f.tag?.trim()) {
      // Case-scope unique by contract; on the rare in-flight duplicate, first wins
      // (deterministic — the resolver never guesses between two same-tag files).
      const key = normalizeTagKey(f.tag);
      if (!byTag.has(key)) byTag.set(key, entry);
    }
  }
  for (const sub of folder.folders) walk(sub, byPath, byBasename, byTag);
}

/** Build the index from a tree root — exported for direct unit testing. */
export function buildWorkspaceAliasMap(root: WorkspaceTreeFolder | undefined): WorkspaceAliasMap {
  const byPath = new Map<string, WorkspaceAliasEntry>();
  const byBasename = new Map<string, WorkspaceAliasEntry | typeof AMBIGUOUS>();
  const byTag = new Map<string, WorkspaceAliasEntry>();
  if (root) walk(root, byPath, byBasename, byTag);

  const resolve = (payload: string): WorkspaceAliasEntry | null => {
    const exact = byPath.get(payload);
    if (exact) return exact;
    const byBase = byBasename.get(basename(payload));
    return byBase && byBase !== AMBIGUOUS ? byBase : null;
  };

  const resolveTag = (tag: string): WorkspaceAliasEntry | null => {
    const key = normalizeTagKey(tag);
    return key === "" ? null : byTag.get(key) ?? null;
  };

  const allTags = (): { entry: WorkspaceAliasEntry; tag: string }[] => {
    const out: { entry: WorkspaceAliasEntry; tag: string }[] = [];
    for (const e of byTag.values()) if (e.tag) out.push({ entry: e, tag: e.tag });
    return out;
  };

  const siblingEffectiveNames = (path: string): string[] => {
    const dir = dirOf(path);
    const out: string[] = [];
    for (const e of byPath.values()) {
      if (e.path === path || dirOf(e.path) !== dir) continue;
      out.push(e.alias?.trim() || e.name);
    }
    return out;
  };

  return { byPath, byBasename, byTag, resolve, resolveTag, allTags, siblingEffectiveNames };
}

export function useWorkspaceAliasMap(): WorkspaceAliasMap {
  const { data } = useCaseWorkspaceTree();
  return useMemo(() => buildWorkspaceAliasMap(data?.root), [data]);
}
